import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";
import { articleJsonLd, siteUrl } from "@/data/seo";
import { getArticle } from "@/data/articles";
import type { ArticleFaq } from "@/data/articles/types";

function FaqItem({ question, children }: { question: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-background-300/60 bg-background-50">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer"
        aria-expanded={open}
      >
        <span className="font-heading text-base font-semibold text-foreground-950">{question}</span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center text-foreground-500">
          <i className={`text-lg transition-transform ${open ? "rotate-45" : ""} ri-add-line`} />
        </span>
      </button>
      {open && <div className="px-5 pb-5 text-sm leading-relaxed text-foreground-700">{children}</div>}
    </div>
  );
}

function RelatedLink({ to, label }: { to: string; label: string }) {
  return to === "/" ? (
    <Link
      to={to}
      className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-100"
    >
      <i className="ri-home-4-line text-base" />
      {label}
    </Link>
  ) : (
    <Link
      to={to}
      className="whitespace-nowrap inline-flex items-center gap-2 rounded-full border border-background-300/70 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
    >
      <i className="ri-arrow-right-line text-base" />
      {label}
    </Link>
  );
}

export default function ArticleDetailPage() {
  const { slug = "" } = useParams();
  const { t, i18n } = useTranslation();
  const article = getArticle(i18n.language, slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="mx-auto w-full max-w-3xl px-4 py-20 text-center md:px-6">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {t("article.notFound")}
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line text-base" />
            {t("article.breadcrumbHome")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd
        data={articleJsonLd({
          headline: article.title,
          description: article.subtitle,
          image: article.image,
          url: siteUrl(`/articles/${article.slug}`),
        })}
      />
      <Header />
      <main className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6 md:py-14">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t("article.breadcrumbHome")}
          </Link>
          <span className="text-foreground-300">/</span>
          <span className="text-foreground-700">{article.category}</span>
        </nav>

        <h1 className="mt-6 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
          {article.title}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-foreground-600 md:text-lg">
          {article.subtitle}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
          <span className="flex items-center gap-2">
            <i className="ri-calendar-line" />
            {t("article.updated")}
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-time-line" />
            {article.readTime} {t("article.readTime")}
          </span>
          <span className="flex items-center gap-2">
            <i className="ri-shield-check-line text-accent-600" />
            USFanslink
          </span>
        </div>

        <div className="mt-8 overflow-hidden rounded-lg border border-background-300/60 bg-background-50">
          <img
            src={article.image}
            alt={article.imageAlt}
            title={`${article.title} - USFans Spreadsheet`}
            className="h-56 w-full object-cover object-top md:h-72"
          />
        </div>

        <article className="mt-10 space-y-10">
          {article.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="font-heading text-2xl font-semibold text-foreground-950">
                {section.heading}
              </h2>
              <p className="mt-3 leading-relaxed text-foreground-700">{section.body}</p>
            </section>
          ))}

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {article.table.title}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{article.table.intro}</p>
            <div className="mt-5 overflow-x-auto rounded-lg border border-background-300/60">
              <table className="w-full min-w-[480px] border-collapse text-left text-sm">
                <thead>
                  <tr className="bg-background-100">
                    {article.table.headers.map((h) => (
                      <th key={h} className="whitespace-nowrap px-4 py-3 font-semibold text-foreground-950">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {article.table.rows.map((row, i) => (
                    <tr key={i} className="border-t border-background-300/60">
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className={`whitespace-nowrap px-4 py-3 ${
                            j === 0 ? "font-medium text-foreground-800" : "text-foreground-600"
                          }`}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.faq.title")}
            </h2>
            <div className="mt-5 space-y-3">
              {article.faq.map((item: ArticleFaq) => (
                <FaqItem key={item.q} question={item.q}>
                  {item.a}
                </FaqItem>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
              {t("article.conclusion.title")}
            </h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{article.conclusion}</p>
          </section>
        </article>

        <div className="mt-12 rounded-lg border border-background-300/60 bg-background-100/60 p-6">
          <p className="font-heading text-base font-semibold text-foreground-950">
            {t("article.relatedTitle")}
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {article.related.map((r) => (
              <RelatedLink key={r.to + r.label} to={r.to} label={r.label} />
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
          <p className="font-heading text-lg font-semibold text-foreground-950">
            {t("article.ctaTitle")}
          </p>
          <p className="mt-2 text-sm text-foreground-600">{t("article.ctaText")}</p>
          <Link
            to="/"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line text-base" />
            {t("article.breadcrumbHome")}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}